"use client";

import { useState, useRef, useEffect } from "react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import {
  X,
  Star,
  ShieldAlert,
  Send,
  Mail,
  Upload,
  FileText,
  Image as ImageIcon,
  Trash2,
  CheckCircle,
  Smile,
  Meh,
  Frown,
  ChevronLeft,
  Loader2,
} from "lucide-react";

type FeedbackType = "rate" | "security" | null;
type Rating = "happy" | "neutral" | "sad" | null;

interface FileAttachment {
  file: File;
  preview?: string;
  type: "image" | "document";
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_FILES = 3;
const MAX_RATING_MESSAGE = 2000;
const MAX_REPORT_MESSAGE = 5000;
const ALLOWED_EXTENSIONS = ["jpg", "jpeg", "png", "webp", "pdf", "doc", "docx"];

const RATING_TO_API: Record<Exclude<Rating, null>, string> = {
  happy: "positiva",
  neutral: "neutral",
  sad: "negativa",
};

type FieldErrors = Record<string, string>;

/**
 * Convierte la respuesta de error del proxy en mensajes para el formulario:
 * el 422 trae `errors.<campo>` (arreglos de strings) y el 429 tiene un texto
 * fijo pedido por el CMS.
 */
async function leerError(
  res: Response,
  t: (key: string) => string,
): Promise<{ general: string; campos: FieldErrors }> {
  if (res.status === 429) return { general: t("errors.rateLimit"), campos: {} };

  const json = await res.json().catch(() => null);
  if (res.status === 422 && json?.errors) {
    const campos: FieldErrors = {};
    for (const [campo, mensajes] of Object.entries(json.errors)) {
      // "adjuntos.0" / "adjuntos[]" se agrupan bajo "adjuntos"
      const clave = campo.split(/[.[]/)[0];
      const texto = Array.isArray(mensajes) ? mensajes[0] : String(mensajes);
      if (!campos[clave]) campos[clave] = texto;
    }
    return { general: t("errors.validation"), campos };
  }

  return { general: t("errors.generic"), campos: {} };
}

export default function FeedbackWidget() {
  const t = useTranslations("feedbackWidget");
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<FeedbackType>(null);
  const [rating, setRating] = useState<Rating>(null);
  const [showRatingForm, setShowRatingForm] = useState(false);
  const [comment, setComment] = useState("");
  const [email, setEmail] = useState("");
  const [reportText, setReportText] = useState("");
  const [reportEmail, setReportEmail] = useState("");
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [fileError, setFileError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const widgetRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        widgetRef.current &&
        !widgetRef.current.contains(event.target as Node)
      ) {
        if (isOpen && !feedbackType) {
          setIsOpen(false);
        }
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, feedbackType]);

  const resetForm = () => {
    setFeedbackType(null);
    setRating(null);
    setShowRatingForm(false);
    setComment("");
    setEmail("");
    setReportText("");
    setReportEmail("");
    attachments.forEach((a) => a.preview && URL.revokeObjectURL(a.preview));
    setAttachments([]);
    setFileError("");
    setSubmitError("");
    setFieldErrors({});
    setIsSuccess(false);
  };

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(resetForm, 300);
  };

  const handleRatingSelect = (selected: Rating) => {
    setRating(selected);
    setShowRatingForm(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    setFileError("");
    const newAttachments: FileAttachment[] = [];

    Array.from(files).forEach((file) => {
      const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
      if (!ALLOWED_EXTENSIONS.includes(extension)) {
        setFileError(t("security.fileTypeNotAllowed"));
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        setFileError(t("security.fileTooLarge"));
        return;
      }

      const isImage = file.type.startsWith("image/");
      const attachment: FileAttachment = {
        file,
        type: isImage ? "image" : "document",
        preview: isImage ? URL.createObjectURL(file) : undefined,
      };
      newAttachments.push(attachment);
    });

    setAttachments((prev) => {
      const combined = [...prev, ...newAttachments];
      if (combined.length > MAX_FILES) {
        setFileError(t("security.tooManyFiles"));
        combined
          .slice(MAX_FILES)
          .forEach((a) => a.preview && URL.revokeObjectURL(a.preview));
      }
      return combined.slice(0, MAX_FILES);
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => {
      const newAttachments = [...prev];
      if (newAttachments[index].preview) {
        URL.revokeObjectURL(newAttachments[index].preview!);
      }
      newAttachments.splice(index, 1);
      return newAttachments;
    });
  };

  const finishSuccess = () => {
    setIsSuccess(true);
    setTimeout(() => {
      handleClose();
    }, 2000);
  };

  const handleSubmitRating = async () => {
    if (!rating) return;

    setIsSubmitting(true);
    setSubmitError("");
    setFieldErrors({});
    try {
      const res = await fetch("/api/experiencia/calificaciones", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          calificacion: RATING_TO_API[rating],
          mensaje: comment.trim(),
          correo: email.trim(),
        }),
      });

      if (!res.ok) {
        const { general, campos } = await leerError(res, t);
        setSubmitError(general);
        setFieldErrors(campos);
        return;
      }
      finishSuccess();
    } catch {
      setSubmitError(t("errors.generic"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitReport = async () => {
    if (!reportText.trim()) return;

    setIsSubmitting(true);
    setSubmitError("");
    setFieldErrors({});
    try {
      const formData = new FormData();
      formData.append("mensaje", reportText.trim());
      if (reportEmail.trim()) formData.append("correo", reportEmail.trim());
      attachments.forEach((a) => formData.append("adjuntos[]", a.file));

      const res = await fetch("/api/experiencia/reportes-seguridad", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData,
      });

      if (!res.ok) {
        const { general, campos } = await leerError(res, t);
        setSubmitError(general);
        setFieldErrors(campos);
        return;
      }
      finishSuccess();
    } catch {
      setSubmitError(t("errors.generic"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const ratingOptions = [
    {
      value: "happy" as Rating,
      icon: Smile,
      color: "text-green-500",
      bgHover: "hover:bg-green-50",
      bgActive: "bg-green-100",
    },
    {
      value: "neutral" as Rating,
      icon: Meh,
      color: "text-amber-500",
      bgHover: "hover:bg-amber-50",
      bgActive: "bg-amber-100",
    },
    {
      value: "sad" as Rating,
      icon: Frown,
      color: "text-red-500",
      bgHover: "hover:bg-red-50",
      bgActive: "bg-red-100",
    },
  ];

  return (
    <div ref={widgetRef} className="fixed bottom-6 left-6 z-[9999]">
      {/* Floating Action Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`group relative w-16 h-16 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center ${
          isOpen
            ? "bg-gray-600 rotate-90"
            : "bg-[#102542a8] hover:bg-[#0c1d35] hover:shadow-[0_6px_28px_rgba(16,37,66,0.65)]"
        }`}
        aria-label="Feedback"
        style={{
          boxShadow: !isOpen ? "0 4px 20px rgba(16, 37, 66, 0.5)" : undefined,
        }}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <Image
            src="/Sugerencias.svg"
            alt="Feedback"
            width={32}
            height={32}
            className="w-8 h-8 text-white"
          />
        )}
        {!isOpen && (
          <span className="absolute -top-1 -left-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
        )}
      </button>

      {/* Menu Panel */}
      <div
        className={`absolute bottom-20 left-0 w-80 max-h-[calc(100dvh-7rem)] flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 origin-bottom-left ${
          isOpen
            ? "scale-100 opacity-100"
            : "scale-90 opacity-0 pointer-events-none"
        }`}
      >
        {/* Success State */}
        {isSuccess && (
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              {t("success.title")}
            </h3>
            <p className="text-gray-500">{t("success.message")}</p>
          </div>
        )}

        {/* Main Menu */}
        {!feedbackType && !isSuccess && (
          <>
            <div className="bg-gradient-to-r from-[var(--fondoGeneral-secciones)] to-[var(--fondoGeneral-secciones)]/80 p-5 text-white">
              <h3 className="text-lg font-bold">{t("title")}</h3>
              <p className="text-white/80 text-sm mt-1">{t("subtitle")}</p>
            </div>
            <div className="p-4 space-y-3">
              <button
                onClick={() => setFeedbackType("rate")}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-gray-50 hover:bg-[var(--fondoGeneral-secciones)]/5 border-2 border-transparent hover:border-[var(--fondoGeneral-secciones)]/20 transition-all group/btn"
              >
                <div className="w-12 h-12 shrink-0 rounded-xl bg-amber-100 flex items-center justify-center group-hover/btn:scale-110 transition-transform">
                  <Star className="w-6 h-6 text-amber-500" />
                </div>
                <div className="text-left">
                  <span className="font-semibold text-gray-800 block">
                    {t("options.rate")}
                  </span>
                  <span className="text-sm text-gray-500">
                    {t("options.rateDesc")}
                  </span>
                </div>
              </button>

              <button
                onClick={() => setFeedbackType("security")}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-gray-50 hover:bg-[var(--fondoGeneral-secciones)]/5 border-2 border-transparent hover:border-[var(--fondoGeneral-secciones)]/20 transition-all group/btn"
              >
                <div className="w-12 h-12 shrink-0 rounded-xl bg-red-100 flex items-center justify-center group-hover/btn:scale-110 transition-transform">
                  <ShieldAlert className="w-6 h-6 text-red-500" />
                </div>
                <div className="text-left">
                  <span className="font-semibold text-gray-800 block">
                    {t("options.security")}
                  </span>
                  <span className="text-sm text-gray-500">
                    {t("options.securityDesc")}
                  </span>
                </div>
              </button>
            </div>
          </>
        )}

        {/* Rate Airport Form */}
        {feedbackType === "rate" && !isSuccess && (
          <div className="flex flex-col flex-1 min-h-0">
            {/* Header */}
            <div className="bg-gradient-to-r from-[var(--fondoGeneral-secciones)] to-[var(--fondoGeneral-secciones)]/80 p-4 text-white flex items-center gap-3 shrink-0">
              <button
                onClick={() => {
                  setSubmitError("");
                  setFieldErrors({});
                  if (showRatingForm) {
                    setShowRatingForm(false);
                    setRating(null);
                  } else {
                    setFeedbackType(null);
                  }
                }}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div>
                <h3 className="font-bold">{t("rate.title")}</h3>
                <p className="text-white/80 text-sm">{t("rate.subtitle")}</p>
              </div>
            </div>

            <div className="p-5 overflow-y-auto min-h-0">
              {!showRatingForm ? (
                /* Rating Selection */
                <div className="space-y-4">
                  <p className="text-center text-gray-600 font-medium">
                    {t("rate.question")}
                  </p>
                  <div className="flex justify-center gap-4">
                    {ratingOptions.map(
                      ({ value, icon: Icon, color, bgHover, bgActive }) => (
                        <button
                          key={value}
                          onClick={() => handleRatingSelect(value)}
                          className={`p-4 rounded-2xl transition-all duration-200 ${bgHover} ${
                            rating === value ? bgActive : "bg-gray-50"
                          } hover:scale-110`}
                        >
                          <Icon
                            className={`w-12 h-12 ${color}`}
                            strokeWidth={1.5}
                          />
                        </button>
                      ),
                    )}
                  </div>
                </div>
              ) : (
                /* Rating Details Form */
                <div className="space-y-4">
                  <div className="flex justify-center mb-2">
                    {ratingOptions.map(
                      ({ value, icon: Icon, color, bgActive }) =>
                        rating === value ? (
                          <div
                            key={value}
                            className={`p-3 rounded-xl ${bgActive}`}
                          >
                            <Icon
                              className={`w-10 h-10 ${color}`}
                              strokeWidth={1.5}
                            />
                          </div>
                        ) : null,
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      {t("rate.tellUsMore")}
                      <span className="text-gray-400 font-normal ml-1">
                        ({t("optional")})
                      </span>
                    </label>
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder={t("rate.commentPlaceholder")}
                      rows={3}
                      maxLength={MAX_RATING_MESSAGE}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--fondoGeneral-secciones)] focus:ring-2 focus:ring-[var(--fondoGeneral-secciones)]/20 outline-none transition-all resize-none text-sm"
                    />
                    {fieldErrors.mensaje && (
                      <p className="text-red-500 text-xs mt-1">
                        {fieldErrors.mensaje}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      <Mail className="w-4 h-4 inline mr-1" />
                      {t("email")}
                      <span className="text-gray-400 font-normal ml-1">
                        ({t("optional")})
                      </span>
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t("emailPlaceholder")}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--fondoGeneral-secciones)] focus:ring-2 focus:ring-[var(--fondoGeneral-secciones)]/20 outline-none transition-all text-sm"
                    />
                    {fieldErrors.correo && (
                      <p className="text-red-500 text-xs mt-1">
                        {fieldErrors.correo}
                      </p>
                    )}
                  </div>

                  {submitError && (
                    <p className="text-red-600 text-sm bg-red-50 rounded-lg px-3 py-2">
                      {submitError}
                    </p>
                  )}

                  <button
                    onClick={handleSubmitRating}
                    disabled={isSubmitting}
                    className="w-full bg-[var(--fondoGeneral-secciones)] text-white py-3 rounded-xl font-semibold hover:bg-[var(--fondoGeneral-secciones)]/90 transition-all flex items-center justify-center gap-2 disabled:opacity-70"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        {t("sending")}
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        {t("submit")}
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Security Report Form */}
        {feedbackType === "security" && !isSuccess && (
          <div className="flex flex-col flex-1 min-h-0">
            {/* Header */}
            <div className="bg-gradient-to-r from-[var(--fondoGeneral-secciones)] to-[var(--fondoGeneral-secciones)]/80 p-4 text-white flex items-center gap-3 shrink-0">
              <button
                onClick={() => {
                  setSubmitError("");
                  setFieldErrors({});
                  setFeedbackType(null);
                }}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div>
                <h3 className="font-bold">{t("security.title")}</h3>
                <p className="text-white/80 text-sm">
                  {t("security.subtitle")}
                </p>
              </div>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto min-h-0">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {t("security.message")}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={reportText}
                  onChange={(e) => setReportText(e.target.value)}
                  placeholder={t("security.messagePlaceholder")}
                  rows={4}
                  maxLength={MAX_REPORT_MESSAGE}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--fondoGeneral-secciones)] focus:ring-2 focus:ring-[var(--fondoGeneral-secciones)]/20 outline-none transition-all resize-none text-sm"
                />
                {fieldErrors.mensaje && (
                  <p className="text-red-500 text-xs mt-1">
                    {fieldErrors.mensaje}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <Mail className="w-4 h-4 inline mr-1" />
                  {t("email")}
                  <span className="text-gray-400 font-normal ml-1">
                    ({t("optional")})
                  </span>
                </label>
                <input
                  type="email"
                  value={reportEmail}
                  onChange={(e) => setReportEmail(e.target.value)}
                  placeholder={t("emailPlaceholder")}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--fondoGeneral-secciones)] focus:ring-2 focus:ring-[var(--fondoGeneral-secciones)]/20 outline-none transition-all text-sm"
                />
                {fieldErrors.correo && (
                  <p className="text-red-500 text-xs mt-1">
                    {fieldErrors.correo}
                  </p>
                )}
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <Upload className="w-4 h-4 inline mr-1" />
                  {t("security.attachments")}
                  <span className="text-gray-400 font-normal ml-1">
                    ({t("optional")})
                  </span>
                </label>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center cursor-pointer hover:border-[var(--fondoGeneral-secciones)]/50 hover:bg-gray-50 transition-all"
                >
                  <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500">
                    {t("security.dropFiles")}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {t("security.maxSize")}
                  </p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx"
                  onChange={handleFileChange}
                  className="hidden"
                />
                {(fileError || fieldErrors.adjuntos) && (
                  <p className="text-red-500 text-xs mt-1">
                    {fileError || fieldErrors.adjuntos}
                  </p>
                )}

                {/* Attachments Preview */}
                {attachments.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {attachments.map((attachment, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg"
                      >
                        {attachment.type === "image" && attachment.preview ? (
                          <img
                            src={attachment.preview}
                            alt="Preview"
                            className="w-10 h-10 rounded object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
                            <FileText className="w-5 h-5 text-blue-500" />
                          </div>
                        )}
                        <span className="text-sm text-gray-600 flex-1 truncate">
                          {attachment.file.name}
                        </span>
                        <button
                          onClick={() => removeAttachment(index)}
                          className="p-1 hover:bg-red-100 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {submitError && (
                <p className="text-red-600 text-sm bg-red-50 rounded-lg px-3 py-2">
                  {submitError}
                </p>
              )}

              <button
                onClick={handleSubmitReport}
                disabled={isSubmitting || !reportText.trim()}
                className="w-full bg-[var(--fondoGeneral-secciones)] text-white py-3 rounded-xl font-semibold hover:bg-[var(--fondoGeneral-secciones)]/90 transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t("sending")}
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    {t("submit")}
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
